/**
 * Countries as the country picker offers them: the dashboard's
 * (web/src/shell/CountryCombobox.tsx) and /get-started's "You live in"
 * (src/public/site/client/country-combobox.ts). One copy of the names, the
 * order, the search and the guess, so the two pickers read and behave the
 * same. No imports, so the public pages' bundle can take it whole.
 */

const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

/** "CA" → "Canada". Falls back to the code for anything Intl doesn't know. */
export function countryName(code: string): string {
  try {
    return REGION_NAMES.of(code) ?? code;
  } catch {
    return code;
  }
}

/** "CA" → 🇨🇦, built from the two regional-indicator symbols. */
export function countryFlag(code: string): string {
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(
    ...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );
}

export interface CountryOption {
  /** ISO code, or '' for "use the guess". */
  value: string;
  name: string;
  flag: string;
}

/** Every country the picker lists, by name. `codes` is countries-and-timezones' list. */
export function countryOptions(codes: readonly string[]): CountryOption[] {
  return codes
    .map((code) => ({
      value: code,
      name: countryName(code),
      flag: countryFlag(code),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The picker's first option while nothing is saved: the guessed country,
 * which choosing keeps unsaved, or "Not set" with no guess.
 */
export function guessOption(guess: string | null): CountryOption {
  return guess
    ? { value: '', name: countryName(guess), flag: countryFlag(guess) }
    : { value: '', name: 'Not set', flag: '' };
}

/** "🇨🇦 Canada", as the field shows a choice. */
export function optionLabel(option: CountryOption): string {
  return `${option.flag} ${option.name}`.trim();
}

/** Case- and accent-insensitive, so "cote" finds Côte d'Ivoire. */
export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Names that start with the query first, then names with a word that starts
 * with it, then any other match. A two-letter query also matches the ISO code,
 * so "gb" finds the United Kingdom and "us" the United States.
 */
export function searchCountries(
  all: readonly CountryOption[],
  query: string,
): CountryOption[] {
  const q = fold(query.trim());
  if (!q) return [...all];
  const ranked: [number, CountryOption][] = [];
  for (const option of all) {
    const name = fold(option.name);
    const rank =
      option.value.toLowerCase() === q
        ? 0
        : name.startsWith(q)
          ? 1
          : name.split(/[\s-]+/).some((word) => word.startsWith(q))
            ? 2
            : name.includes(q)
              ? 3
              : null;
    if (rank !== null) ranked.push([rank, option]);
  }
  return ranked.sort((a, b) => a[0] - b[0]).map(([, option]) => option);
}

/** Where the viewer's country came from, strongest first. */
export type CountrySource = 'saved' | 'ip' | 'timezone';

const GUESSED_FROM: Record<Exclude<CountrySource, 'saved'>, string> = {
  ip: 'your connection',
  timezone: 'your time zone',
};

/** Said under the country picker until the user picks one. */
export function guessedNote(source: Exclude<CountrySource, 'saved'>): string {
  return `Guessed from ${GUESSED_FROM[source]}. Pick yours to be sure.`;
}
